import type { ComponentType, ReactNode } from 'react';

/**
 * Upstream-only modules: src/modules/<name>/index.tsx, listed in .upstream-only.
 * A module is in the bundle because its folder exists; children have none.
 * See docs/superpowers/specs/2026-09-25-upstream-only-modules-design.md.
 */

export interface ModuleRoute {
  /** Without a leading slash: "canary", "canary/products/:id" */
  path: string;
  /** The page, lazy() inside the module; routes.tsx adds the Suspense */
  element: ReactNode;
  roles?: string[];
  /** A page of its own: signed-in only, but outside the app layout and its sidebar */
  standalone?: boolean;
}

export interface ModuleNavItem {
  path: string;
  labelKey: string;
  defaultLabel: string;
  icon: ComponentType<{ className?: string }>;
  roles?: string[];
  /** Only for accounts that hold one of `roles`, the super admin included: a page for a kind of user (a driver's own loads), not a page to manage */
  ownRolesOnly?: boolean;
  qaId: string;
}

export interface ModuleOverlay {
  /** Rendered inside the app layout over every page, e.g. a floating panel */
  element: ReactNode;
  /** Who sees it, as for a nav item (the super admin passes) */
  roles?: string[];
}

export interface ModuleManifest {
  routes: ModuleRoute[];
  navItems: ModuleNavItem[];
  overlays?: ModuleOverlay[];
  /** Shown on Početna (the home page), one under another, for the roles that may see them */
  homeWidgets?: ModuleOverlay[];
}

export function collectModules(found: Record<string, { default: ModuleManifest }>) {
  const manifests = Object.keys(found)
    .sort()
    .map((key) => found[key].default);
  const routes = manifests.flatMap((manifest) => manifest.routes);
  const moduleNavItems = manifests.flatMap((manifest) => manifest.navItems);
  return {
    /** Rendered inside the app layout */
    moduleRoutes: routes.filter((route) => !route.standalone),
    /** Rendered on their own, see ModuleRoute.standalone */
    standaloneModuleRoutes: routes.filter((route) => route.standalone),
    moduleNavItems,
    /** Rendered by AppLayout over every page, filtered by role */
    moduleOverlays: manifests.flatMap((manifest) => manifest.overlays ?? []),
    /** Rendered by Home, filtered by role */
    moduleHomeWidgets: manifests.flatMap((manifest) => manifest.homeWidgets ?? []),
    // Spread into AppLayout's path → qa-id maps, so its render code stays as is
    moduleQaIds: Object.fromEntries(moduleNavItems.map((item) => [item.path, item.qaId])) as Record<string, string>,
  };
}
